import { RowStateType } from "../enums/row-state-type";
import { IRowState } from "../interfaces/row-state";
import { CommonRowActions } from "../enums/common-row-actions";
import { IListSortOption } from "../interfaces/list-sort-option";
import {
    BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, startWith, Subject
} from "rxjs";
import { RxjsUtil } from "../../utils/rxjs-util";
import { IListStateService } from "../interfaces/list-state-service";
import { ListUtil } from "../util/list-util";
import {
    IBulkListActionOptions, IBulkUpdateListActionOptions
} from "../interfaces/bulk-list-action-options";
import {
    BulkResponse, BulkResponseItem
} from "@shopliftr/common-js/shared";
import {
    IListFilterRequest, IPagedListFilterRequest, IUnpagedListFilterRequest
} from "../interfaces/list-filter-request";
import {
    Injectable, OnDestroy, inject
} from "@angular/core";
import {
    ArrayUtil, ObjectUtil, StringUtil
} from "@shopliftr/common-js/data";
import { InvalidParametersException } from "../../exceptions/invalid-parameters-exception";
import { InvalidControlStateException } from "../../exceptions/invalid-control-state-exception";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { ListActionBehavior } from "../enums/list-action-behavior";
import { ListDeleteBehavior } from "../enums/list-delete-behavior";
import { DEFAULT_PAGE_SIZE } from "../constants/constants";
import { IListInitOptions } from "../interfaces/list-init-options";
import {
    IPagedListInitOptions, IPagedSearchListInitOptions, IPagedSharedListInitOptions
} from "../interfaces/paged-list-init-options";
import {
    IUnpagedListInitOptions, IUnpagedSearchListInitOptions, IUnpagedSharedListInitOptions
} from "../interfaces/unpaged-list-init-options";
import { IListActionEvent } from "../interfaces/list-action-event";
import { ListActionMessage } from "../interfaces/list-action-message";
import { IListCacheEntry } from "../interfaces/list-cache-entry";
import { ListSortUtil } from "../util/list-sort-util";

/**
 * All state services concerned with lists should extend from this class
 */
@Injectable()
export abstract class AbstractListStateService<M extends object, O extends object, R extends object = M>
implements IListStateService<M, O, R>, OnDestroy {

    /**
     * The order of precedence for the Row State
     *
     */
    static readonly ROW_STATE_TYPE_ORDER = [
        RowStateType.Error,
        RowStateType.Warning,
        RowStateType.Disabled,
        RowStateType.Highlighted,
        RowStateType.Tooltip
    ];

    /**
     * App Service injector used for error handling
     */
    protected readonly _app = inject(COMMON_APP_SERVICE);

    /**
     * Human-readable label derived from the model's static `modelName` property.
     * Set lazily by `_deriveModelLabel` on the first call that provides a model instance.
     */
    protected _modelLabel: string | undefined;

    /**
     * Whether the list has been initialized in paged mode
     */
    get paged(): boolean {
        return this._paged;
    }

    /**
     * Whether the list has been initialized in paged mode
     */
    private _paged = false;

    /**
     * Whether results should be cached
     */
    get cached(): boolean {
        return this._cached;
    }

    /**
     * Whether results should be cached
     */
    private _cached = false;

    /**
     * The single-entry cache slot. Populated by {@link writeCache}, cleared by {@link clearCache}.
     */
    private _cacheEntry: IListCacheEntry<M, O> | undefined;

    /**
     * The current cache entry, or `undefined` when the cache is empty.
     */
    get cacheEntry(): IListCacheEntry<M, O> | undefined {
        return this._cacheEntry;
    }

    /**
     * What the list does after a record is created
     */
    get createBehavior(): ListActionBehavior {
        return this._createBehavior;
    }

    /**
     * What the list does after a record is created
     */
    private _createBehavior: ListActionBehavior = ListActionBehavior.Default;

    /**
     * What the list does after a record is updated
     */
    get updateBehavior(): ListActionBehavior {
        return this._updateBehavior;
    }

    /**
     * What the list does after a record is updated
     */
    private _updateBehavior: ListActionBehavior = ListActionBehavior.Default;

    /**
     * What the list does after a record is deleted
     */
    get deleteBehavior(): ListDeleteBehavior {
        return this._deleteBehavior;
    }

    /**
     * What the list does after a record is deleted
     */
    private _deleteBehavior: ListDeleteBehavior = ListDeleteBehavior.Default;

    /**
     * This is the page size that the system will reset to
     */
    get defaultPageSize(): number | undefined {
        return this._defaultPageSize;
    }

    /**
     * This is the page size that the system will reset to
     */
    private _defaultPageSize: number | undefined = undefined;

    /**
     * The sort option that will be used by default
     */
    get defaultSortOption(): IListSortOption {
        return this._defaultSortOption;
    }

    /**
     * The sort option that will be used by default
     */
    private _defaultSortOption: IListSortOption;

    /**
     * The available sort options for the list
     */
    get sortOptions(): Array<IListSortOption> {
        return this._sortOptions;
    }

    /**
     * The available sort options for the list
     */
    private _sortOptions: Array<IListSortOption>;

    /**
     * Options used to filter the list
     *
     * This behavior subject maintains the state
     */
    private readonly _filterOptions$ = new BehaviorSubject<O>(undefined);

    private readonly _rowStateChange$ = new Subject<void>();

    /**
     * Emits when the any row state changes
     */
    get rowStateChange$(): Observable<void> {

        return this._rowStateChange$.asObservable();
    }

    /**
     * Options used to filter the list
     */
    get filterOptions$(): Observable<O> {
        return this._filterOptions$.asObservable().pipe(
            // Only emit next event when the value has changed
            RxjsUtil.distinctUntilObjectChanged()
        );
    }

    /**
     * Options used to filter the list
     */
    get filterOptions(): O {
        return this._filterOptions$.getValue();
    }

    /**
     * The total number of records (no pagination)
     *
     * This behavior subject maintains the state
     */
    private readonly _searchTotal$ = new BehaviorSubject<number>(0);

    /**
     * The total number of records (no pagination)
     */
    get searchTotal$(): Observable<number> {
        return this._searchTotal$.asObservable().pipe(
            // Only emit next event when the value has changed
            distinctUntilChanged()
        );
    }

    /**
     * The total number of records (no pagination).
     * When the list is pinned, returns the count of pinned models instead.
     */
    get searchTotal(): number {
        return this.isPinned ? this._pinnedModels$.value.length : this._searchTotal$.getValue();
    }

    /**
     * The set of pinned models.
     * Non-empty when a create/update action uses the Filter behavior.
     */
    private readonly _pinnedModels$ = new BehaviorSubject<Array<M>>([]);

    /**
     * The currently pinned models.
     */
    get pinnedModels(): Array<M> {
        return this._pinnedModels$.value;
    }

    /**
     * Observable of the currently pinned models.
     */
    get pinnedModels$(): Observable<Array<M>> {
        return this._pinnedModels$.asObservable();
    }

    /**
     * True when one or more models are pinned.
     */
    get isPinned(): boolean {
        return this._pinnedModels$.value.length > 0;
    }

    /**
     * The size of a page in the list
     *
     * This behavior subject maintains the state
     */
    private readonly _pageSize$ = new BehaviorSubject<number>(undefined);

    /**
     * The size of a page in the list
     */
    get pageSize$(): Observable<number> {
        return this._pageSize$.asObservable().pipe(
            // Only emit next event when the value has changed
            distinctUntilChanged()
        );
    }

    /**
     * The size of a page in the list
     */
    get pageSize(): number {
        return this._pageSize$.getValue();
    }

    /**
     * The current page of the list
     *
     * This behavior subject maintains the state
     */
    private readonly _pageIndex$ = new BehaviorSubject<number>(0);

    /**
     * The current page of the list
     */
    get pageIndex$(): Observable<number> {
        return this._pageIndex$.asObservable().pipe(
            // Only emit next event when the value has changed
            distinctUntilChanged()
        );
    }

    /**
     * The current page of the list
     */
    get pageIndex(): number {
        return this._pageIndex$.getValue();
    }

    /**
     * The sort option for the list
     *
     * This behavior subject maintains the state
     */
    private readonly _sort$ = new BehaviorSubject<IListSortOption>(undefined);

    /**
     * The sort option for the list
     */
    get sort$(): Observable<IListSortOption> {
        return this._sort$.asObservable().pipe(
            RxjsUtil.distinctUntilObjectChanged()
        );
    }

    /**
     * The sort option for the list
     */
    get sort(): IListSortOption {
        return this._sort$.getValue();
    }

    /**
     * A List Filter request for the state of the list
     *
     * If filterOptions is undefined it will return undefined. `sort` is optional and
     * may be undefined when no sort has been applied yet.
     *
     * This emit next when the filter options, sort, pageIndex, or pageSize changes
     */
    get filterRequest$(): Observable<IListFilterRequest<O>> {

        return combineLatest([
            this.filterOptions$,
            this.pageIndex$,
            this.pageSize$,
            this.sort$
        ]).pipe(
            RxjsUtil.debounceAfterInitial(0),
            map(([options, pageIndex, pageSize, sort]) =>
                this._newFilterRequest(options, pageIndex, pageSize, sort)),
            RxjsUtil.distinctUntilObjectChanged()
        );
    }

    /**
     * A List Filter request for the state of the list
     *
     * If filterOptions is undefined it will return undefined. `sort` is optional and
     * may be undefined when no sort has been applied yet.
     *
     */
    get filterRequest(): IListFilterRequest<O> {
        return this._newFilterRequest(this.filterOptions, this.pageIndex, this.pageSize, this.sort);
    }

    private readonly _rowStateById = new Map<string, Array<IRowState>>();

    private readonly _infoHeaderState$ = new BehaviorSubject<IRowState>(undefined);

    /**
     * The the state of the info Header
     *
     * If there are errors or warnings in the rows the infoHeader will be set appropriately
     */
    get infoHeaderState(): IRowState {

        return this._infoHeaderState$.value;
    }

    /**
     * The the state of the info Header
     *
     * If there are errors or warnings in the rows the infoHeader will be set appropriately
     */
    get infoHeaderState$(): Observable<IRowState> {

        return this._infoHeaderState$.asObservable().pipe(
            // Only emit next event when the value has changed
            RxjsUtil.distinctUntilObjectChanged()
        );
    }

    /**
     * Returns the from value based on the pagIndex and pageSize
     * will return 0 if either is not set
     */
    get from(): number {

        return ListUtil.getFromValue(this.pageIndex, this.pageSize);
    }

    /**
     * True if the paged list has been initialized
     */
    get listInitialized(): boolean {
        return this._listInitialized;
    }

    /**
     * True if the paged list has been initialized
     */
    private _listInitialized = false;

    /**
     * The id of the row that should be scrolled into view
     */
    get scrollToRowId(): string | undefined {
        return this._scrollToRowId;
    }

    /**
     * The id of the row that should be scrolled into view
     */
    private _scrollToRowId: string | undefined;

    /**
     * Pending scroll-to-row action that should emit a warning if the row is not rendered.
     */
    private _pendingNotRenderedAction: {
        id: string;
        verb: string;
        model: M | undefined;
        notRenderedMessage?: ListActionMessage<M>;
    } | undefined;

    /**
     * The 0-n position of each rendered row, keyed by row id. Rebuilt on every list load
     * by {@link setRowPositions}.
     */
    private readonly _rowPositionById = new Map<string, number>();

    /**
     * The rendered row ids in position order. Rebuilt on every list load by
     * {@link setRowPositions} and used to resolve a pending scroll-to-position.
     */
    private _rowIdByPosition: Array<string> = [];

    /**
     * The list position that should be scrolled to after the next load, set by a delete
     * running under {@link ListDeleteBehavior.ScrollToSpot}. Resolved to a row id by
     * {@link setRowPositions} once the reloaded rows arrive.
     */
    private _scrollToPosition: number | undefined;

    /**
     * True if the paged search list has been initialized
     */
    get searchInitialized(): boolean {
        return this._searchInitialized;
    }

    /**
     * True if the paged search list has been initialized
     */
    private _searchInitialized = false;

    private readonly _listAction$ = new Subject<IListActionEvent<M>>();

    /**
     * Push channel for list action events.
     * Emits after the cache invalidation matrix has been applied by this service. A subscribed
     * ListDataSourceService reloads and re-applies the row states carried in the event; when no
     * data source is subscribed (for example the list view is destroyed while a form route is
     * open) the cache has still been updated, so the list is correct when it is re-created.
     */
    get listAction$(): Observable<IListActionEvent<M>> {
        return this._listAction$.asObservable();
    }

    /**
     * Applies the cache invalidation matrix for the event, then emits it on `listAction$` to
     * trigger a reload-then-reapply cycle in a subscribed data source.
     *
     * @param event - The action event describing which rows are affected and their target states.
     */
    triggerListAction(event: IListActionEvent<M>): void {
        this._emitListAction(event);
    }

    /**
     * When the service is destroyed complete the subjects
     */
    ngOnDestroy(): void {
        this._filterOptions$.complete();
        this._searchTotal$.complete();
        this._pageSize$.complete();
        this._pageIndex$.complete();
        this._sort$.complete();
        this._infoHeaderState$.complete();
        this._rowStateChange$.complete();
        this._listAction$.complete();
        this._pinnedModels$.complete();
    }

    /**
     * Updated the filter options
     */
    updateFilterOptions(options: O): void {
        this.clearPinnedModels();
        this.clearCache();
        const currentOptions = this.filterOptions;
        this._filterOptions$.next(options);
        if (!ObjectUtil.equals(currentOptions, options) && this._paged) {
            this.updatePageIndex(0);
        }
    }

    /**
     * Update the total number of records
     */
    updateSearchTotal(total: number): void {
        this._searchTotal$.next(total);
    }

    /**
     * Pin the given models so the list displays only them.
     *
     * @param models - The models to pin.
     */
    pinModels(models: Array<M>): void {
        this._pinnedModels$.next(models);
    }

    /**
     * Clear the pinned model set, releasing the pin.
     *
     * Does nothing when no model is pinned: an empty emission on `pinnedModels$` makes the list
     * reload, so emitting it from every filter, sort and page change sent an extra request that
     * still carried the previous state.
     */
    clearPinnedModels(): void {
        if (this._pinnedModels$.value.length) {
            this._pinnedModels$.next([]);
        }
    }

    /**
     * Update the size of a page in the list
     */
    updatePageSize(size: number): void {
        this.clearPinnedModels();
        this.clearCache();
        if (!this._paged) {
            throw new InvalidControlStateException("updatePageSize called on an unpaged list");
        }
        const currentPageSize = this.pageSize;
        const newPageSize = size ?? this._defaultPageSize;
        this._pageSize$.next(newPageSize);
        if (currentPageSize !== newPageSize) {
            this.updatePageIndex(0);
        }
    }

    /**
     * Update the current page of the list
     */
    updatePageIndex(index: number): void {
        this.clearPinnedModels();
        this.clearCache();
        if (!this._paged) {
            throw new InvalidControlStateException("updatePageIndex called on an unpaged list");
        }
        this._pageIndex$.next(index ?? 0);
    }

    /**
     * Update the sort option for the list
     *
     * If sortOption not provided it will reset to the default
     */
    updateSort(sortOption?: IListSortOption): void {
        this.clearPinnedModels();
        this.clearCache();
        const currentSort = this.sort;
        if (!sortOption) {
            sortOption = this._defaultSortOption;
        }
        this._sort$.next(sortOption);
        if (!ObjectUtil.equals(currentSort, sortOption) && this._paged) {
            this.updatePageIndex(0);
        }
    }

    /**
     * Initialize the service for a paged list using an options object.
     * This should be run in ngOnInit for the list component.
     *
     * @param options - Initialization options including filterOptions, sortOptions, etc.
     */
    initializePagedList(options: IPagedListInitOptions<O>): void {

        if (options.filterOptions === undefined || options.filterOptions === null) {
            throw new InvalidParametersException("filterOptions is required");
        }
        this._paged = true;
        this._applyInitOptions(options);
        const pageSize = options.defaultPageSize ?? DEFAULT_PAGE_SIZE;
        this._defaultPageSize = pageSize;
        this._filterOptions$.next(options.filterOptions);
        this.updatePageSize(pageSize);
        this._listInitialized = true;
        this._searchInitialized = false;
    }

    /**
     * Initialize the service for a paged search list using an options object.
     * Unlike `initializePagedList`, this does not require filter options because
     * the search list does not load data until the user submits a search.
     *
     * @param options - Initialization options including sortOptions, etc.
     */
    initializePagedSearchList(options: IPagedSearchListInitOptions): void {

        this._paged = true;
        this._applyInitOptions(options);
        const pageSize = options.defaultPageSize ?? DEFAULT_PAGE_SIZE;
        this._defaultPageSize = pageSize;
        this.updatePageSize(pageSize);
        this._filterOptions$.next(undefined);
        this._searchInitialized = true;
        this._listInitialized = false;
    }

    /**
     * Initialize the service for either a paged list or a paged search list,
     * depending on the host component's `searchList` flag.
     *
     * @param options - Initialization options including searchList flag, filterOptions, sortOptions, etc.
     */
    initializePagedSharedList(options: IPagedSharedListInitOptions<O>): void {
        if (options.searchList) {
            this.initializePagedSearchList(options);
        }
        else {
            this.initializePagedList(options);
        }
        this._checkSharedInitialized(options.searchList);
    }

    /**
     * Initialize the service for an unpaged list using an options object.
     * This should be run in ngOnInit for the list component.
     *
     * @param options - Initialization options including filterOptions, sortOptions, etc.
     */
    initializeUnpagedList(options: IUnpagedListInitOptions<O>): void {

        if (options.filterOptions === undefined || options.filterOptions === null) {
            throw new InvalidParametersException("filterOptions is required");
        }
        this._paged = false;
        this._applyInitOptions(options);
        this._defaultPageSize = undefined;
        this._filterOptions$.next(options.filterOptions);
        this._listInitialized = true;
        this._searchInitialized = false;
    }

    /**
     * Initialize the service for an unpaged search list using an options object.
     * Unlike `initializeUnpagedList`, this does not require filter options because
     * the search list does not load data until the user submits a search.
     *
     * @param options - Initialization options including sortOptions, etc.
     */
    initializeUnpagedSearchList(options: IUnpagedSearchListInitOptions): void {

        this._paged = false;
        this._applyInitOptions(options);
        this._defaultPageSize = undefined;
        this._filterOptions$.next(undefined);
        this._searchInitialized = true;
        this._listInitialized = false;
    }

    /**
     * Initialize the service for either an unpaged list or an unpaged search list,
     * depending on the host component's `searchList` flag.
     *
     * @param options - Initialization options including searchList flag, filterOptions, sortOptions, etc.
     */
    initializeUnpagedSharedList(options: IUnpagedSharedListInitOptions<O>): void {
        if (options.searchList) {
            this.initializeUnpagedSearchList(options);
        }
        else {
            this.initializeUnpagedList(options);
        }
        this._checkSharedInitialized(options.searchList);
    }

    /**
     * Returns the initialization state for a shared list, selecting the
     * appropriate flag based on the host component's `searchView` mode.
     *
     * @param searchList - When true (i.e. the component's `searchView` input is true),
     * returns `searchInitialized`; otherwise returns `listInitialized`.
     */
    checkSharedInitialized(searchList: boolean): boolean {
        return searchList ? this.searchInitialized : this.listInitialized;
    }

    /**
     * Reset the list back to its initial state
     */
    reset(): void {
        this.clearPinnedModels();
        this.clearCache();
        this._rowStateById.clear();
        this._filterOptions$.next(undefined);
        this._pageIndex$.next(0);
        this._pageSize$.next(undefined);
        this._searchTotal$.next(0);
        this._sort$.next(undefined);
        this._infoHeaderState$.next(undefined);
        this._defaultSortOption = undefined;
        this._defaultPageSize = undefined;
        this._listInitialized = false;
        this._searchInitialized = false;
        this._paged = false;
        this._cached = false;
        this._createBehavior = ListActionBehavior.Default;
        this._updateBehavior = ListActionBehavior.Default;
        this._deleteBehavior = ListDeleteBehavior.Default;
        this._scrollToRowId = undefined;
        this._pendingNotRenderedAction = undefined;
        this._rowPositionById.clear();
        this._rowIdByPosition = [];
        this._scrollToPosition = undefined;
    }

    /**
     * Returns the cached entry when its request deeply equals the given request,
     * or `undefined` on a miss.
     *
     * @param request - The current filter request to match against the cache.
     */
    readCache(request: IListFilterRequest<O>): IListCacheEntry<M, O> | undefined {
        if (!this._cacheEntry) {
            return undefined;
        }
        return ObjectUtil.equals(this._cacheEntry.request, request) ? this._cacheEntry : undefined;
    }

    /**
     * Writes a new cache entry. No-op when `cached` is `false`.
     *
     * @param request - The filter request that produced the results.
     * @param results - The page of results to cache.
     * @param total - The total record count.
     */
    writeCache(request: IListFilterRequest<O>, results: Array<M>, total: number): void {
        if (!this._cached) {
            return;
        }
        this._cacheEntry = {
            request,
            results,
            total
        };
    }

    /**
     * Clears the cache entry, forcing the next load to hit the API.
     */
    clearCache(): void {
        this._cacheEntry = undefined;
    }

    /**
     * Inserts a newly created model into the cached result set and re-sorts.
     * No-op when the cache is empty.
     *
     * @param model - The newly created model to insert.
     */
    cacheInsert(model: M): void {
        if (!this._cacheEntry) {
            return;
        }
        const comparator = ListSortUtil.comparator<M>(this.sort);
        const newResults = [...this._cacheEntry.results, model];
        if (comparator) {
            newResults.sort(comparator);
        }
        this._cacheEntry = {
            ...this._cacheEntry,
            results: newResults,
            total: this._cacheEntry.total + 1
        };
    }

    /**
     * Removes a model from the cached result set by its id.
     * No-op when the cache is empty.
     *
     * @param id - The id of the model to remove.
     */
    cacheRemove(id: string): void {
        if (!this._cacheEntry) {
            return;
        }
        this._cacheEntry = {
            ...this._cacheEntry,
            results: this._cacheEntry.results.filter((m) => this._getRowId(m) !== id),
            total: this._cacheEntry.total - 1
        };
    }

    /**
     * Replaces a model in the cached result set with an updated version.
     * No-op when the cache is empty.
     *
     * The updated model takes the original's position, so a row whose id changed ends up under its
     * new id with the old id removed. When the updated id is already cached on another row, that row
     * is dropped (and `total` reduced) so the cache never holds two rows with the same id. When the
     * original is not cached, only a row already carrying the updated id is replaced.
     *
     * @param original - The model before the update (used to find the position by id).
     * @param updated - The replacement model.
     */
    cacheReplace(original: M, updated: M): void {
        if (!this._cacheEntry) {
            return;
        }
        const originalId = this._getRowId(original);
        const updatedId = this._getRowId(updated);
        const hasOriginal = this._cacheEntry.results.some((m) => this._getRowId(m) === originalId);
        const results: Array<M> = [];
        this._cacheEntry.results.forEach((m) => {
            const id = this._getRowId(m);
            if (id === originalId || (!hasOriginal && id === updatedId)) {
                results.push(updated);
            }
            else if (id !== updatedId) {
                results.push(m);
            }
        });
        this._cacheEntry = {
            ...this._cacheEntry,
            results,
            total: this._cacheEntry.total - (this._cacheEntry.results.length - results.length)
        };
    }

    /**
     * Re-sorts the cached result set using the current sort option's comparator.
     * No-op when the cache is empty or no comparator is available.
     */
    cacheResort(): void {
        if (!this._cacheEntry) {
            return;
        }
        const comparator = ListSortUtil.comparator<M>(this.sort);
        if (!comparator) {
            return;
        }
        this._cacheEntry = {
            ...this._cacheEntry,
            results: [...this._cacheEntry.results].sort(comparator)
        };
    }

    /**
     * The list position that should be scrolled to after the next load, or `undefined` when
     * no position scroll is pending. Cleared once {@link setRowPositions} resolves it to a
     * row id.
     */
    get scrollToPositionIndex(): number | undefined {
        return this._scrollToPosition;
    }

    /**
     * Records the 0-n position of every rendered row, replacing any positions from a previous
     * load. Called by the data source on each emission of the rendered rows, which is the
     * single point every load path funnels through: cache hits, fetched results, pinned
     * models, localized re-emissions and error paths.
     *
     * When a position scroll is pending (set by a delete running under
     * {@link ListDeleteBehavior.ScrollToSpot}) it is resolved against the new positions here,
     * so the row that moved into the vacated spot becomes the scroll target.
     *
     * @param rows - The rows about to be rendered, in display order. Accepts models or
     * localized row clones.
     */
    setRowPositions(rows: Array<M | R>): void {
        this._rowPositionById.clear();
        this._rowIdByPosition = (rows ?? []).map((row, index) => {
            const id = this._checkRowOrId(row);
            this._rowPositionById.set(id, index);
            return id;
        });
        this._resolveScrollToPosition();
    }

    /**
     * Returns the 0-n position of the given row in the current list, or `undefined` when the
     * row is not among the rendered rows.
     * Accepts a model, a localized row clone, or a plain string id.
     */
    getRowPosition(rowOrId: M | R | string): number | undefined {
        return this._rowPositionById.get(this._checkRowOrId(rowOrId));
    }

    /**
     * Set the row that should be scrolled into view after the next render.
     * Accepts a model, a localized row clone, or a plain string id.
     */
    scrollToRow(rowOrId: M | R | string): void {
        this._scrollToRowId = this._checkRowOrId(rowOrId as M | string);
    }

    /**
     * Clear the pending scroll-to-row id and any pending scroll-to-position.
     */
    clearScrollToRow(): void {
        this._scrollToRowId = undefined;
        this._scrollToPosition = undefined;
    }

    /**
     * Returns true when the resolved id of `row` matches the given `id`.
     * Accepts a model or a localized row clone.
     */
    matchesRow(row: M | R, id: string): boolean {
        return this._checkRowOrId(row as M) === id;
    }

    /**
     * Called by the table directive after a scroll target is successfully scrolled into view.
     * Clears the pending not-rendered action so no warning is emitted.
     *
     * @param id - The id of the row that was successfully rendered and scrolled to.
     */
    rowRendered(id: string): void {
        if (this._pendingNotRenderedAction?.id === id) {
            this._pendingNotRenderedAction = undefined;
        }
    }

    /**
     * Called by the table directive when a requested scroll target is not yet rendered.
     * When a pending action matches the id, emits a warning notification and clears the pending action.
     *
     * @param id - The id of the row that was not found in the rendered list.
     */
    rowNotRendered(id: string): void {
        const pending = this._pendingNotRenderedAction;
        this._pendingNotRenderedAction = undefined;
        if (!pending || pending.id !== id) {
            return;
        }
        const copy = this._resolveMessage(
            pending.notRenderedMessage,
            pending.model as M,
            `${this._modelLabelOrDefault} ${pending.verb}, but is not shown in the current list`
        );
        this._app.notificationService.displayWarningMessage(copy);
    }

    /**
     * Add a row state for the row based on the given id
     */
    addRowState(rowOrId: string | M | R, rowState: IRowState): void {

        const id = this._checkRowOrId(rowOrId);
        this._removeRowState(id, rowState.action);
        let states = this._rowStateById.get(id);
        if (states) {
            states.push(rowState);
            // Maintain the order of precedence of the state for the given row
            states = states.sort((a, b) => {

                const aIndex = AbstractListStateService.ROW_STATE_TYPE_ORDER.indexOf(a.type);
                const bIndex = AbstractListStateService.ROW_STATE_TYPE_ORDER.indexOf(b.type);
                if (aIndex === bIndex) {
                    return 0;
                }
                else if (aIndex < bIndex) {
                    return -1;
                }
                else {
                    return 1;
                }
            });
        }
        else {
            states = [rowState];
        }
        this._rowStateById.set(id, states);
        this._updateInfoHeaderState();
        this._rowStateChange$.next();
    }

    /**
     * Remove row states for the given id
     *
     * If an action is provided it will only remove states for the given action
     */
    removeRowState(rowOrId: string | M | R, action?: CommonRowActions): void {

        this._removeRowState(rowOrId, action);
        this._rowStateChange$.next();
    }

    /**
     * Remove row states for all rows
     *
     * If one or more action is provided it will only remove states for the given actions
     */
    clearRowStates(...actions: Array<CommonRowActions>): void {

        if (actions.length) {
            this._rowStateById.forEach((states, id, map) => map.set(id, states?.filter((state) => !actions.includes(state.action))));
        }
        else {
            this._rowStateById.clear();
        }
        this._updateInfoHeaderState();
        this._rowStateChange$.next();
    }

    /**
     * Return the state for the given row
     *
     * If the action is provided it will return the state for that action
     *
     * If no action is provided it will return the first state in order of action precedence
     *
     * If not state is found it will return undefined
     */
    getRowState(rowOrId: string | M | R, action?: CommonRowActions): IRowState {

        const id = this._checkRowOrId(rowOrId);
        const states = this._rowStateById.get(id);
        let state: IRowState;
        if (states?.length) {
            if (action) {
                state = states.find((value) => value.action === action);
            }
            else {
                state = states[0];
            }
        }
        return state;
    }

    /**
     * Returns an observable that will emit when the specific row state changes
     * Row state can be undefined
     */
    getRowState$(rowOrId: string | M | R, action?: CommonRowActions): Observable<IRowState> {

        const id = this._checkRowOrId(rowOrId);
        return this._rowStateChange$.asObservable().pipe(
            // This will make sure the current row state or undefined on subscription
            startWith(undefined),
            // Map to the current row state
            map(() => this.getRowState(id, action)),
            // Only emit when this particular row state has changed
            RxjsUtil.distinctUntilObjectChanged()
        );
    }

    /**
     * Returns true if there is a row with the given state type
     *
     * If action is provided it will only return true if there is a row with the given state type and action
     */
    hasRowState(type: RowStateType, action?: CommonRowActions): boolean {

        for (const states of this._rowStateById.values()) {
            const found = states?.find((state) => state.type === type && (!action || action === state.action));
            if (found) {
                return true;
            }
        }
        return false;
    }

    /**
     * Returns the ids of every row currently holding the given state type and/or action.
     *
     * Unlike `hasRowState`, which only reports whether a matching row exists, this returns which
     * rows they are — e.g. so a caller can scope a list/search to the row(s) a prior action
     * (such as a just-completed create) flagged. Either parameter may be omitted to match on the
     * other alone; omitting both returns the ids of every row with any state.
     */
    getRowIds(type?: RowStateType, action?: CommonRowActions): Array<string> {

        const ids: Array<string> = [];
        this._rowStateById.forEach((states, id) => {
            const found = states?.find((state) => (!type || state.type === type) && (!action || state.action === action));
            if (found) {
                ids.push(id);
            }
        });
        return ids;
    }

    /**
     * Records that a model was created: sets a Highlighted/Create row state, displays a
     * success notification, and emits a Create event on `listAction$`.
     *
     * @param model - The newly created model.
     * @param message - Optional notification message (string or function receiving the model).
     * @param notRenderedMessage - Optional message shown when the created record is not among the rendered rows.
     */
    createModel(model: M, message?: ListActionMessage<M>, notRenderedMessage?: ListActionMessage<M>): void {
        this._deriveModelLabel(model);
        const id = this._checkRowOrId(model);
        const rowMessage = this._resolveMessage(message, model, `${this._modelLabelOrDefault} Created`);
        this.addRowState(id, {
            type: RowStateType.Highlighted,
            action: CommonRowActions.Create,
            message: rowMessage
        });
        this._app.notificationService.displaySuccessMessage(rowMessage);
        if (this._createBehavior === ListActionBehavior.Default) {
            this.scrollToRow(id);
            this._pendingNotRenderedAction = {
                id,
                verb: "Created",
                model,
                notRenderedMessage
            };
        }
        else if (this._createBehavior === ListActionBehavior.Filter) {
            this.pinModels([model]);
        }
        this._emitListAction({
            action: CommonRowActions.Create,
            models: [model],
            ids: [id],
            behavior: this._createBehavior,
            rowStates: new Map([[id, this.getRowState(id)]])
        });
    }

    /**
     * Records that a model was updated: sets a Highlighted/Update row state on the updated
     * model, displays a success notification, and emits an Update event on `listAction$`.
     *
     * @param original - The model before the update (used for removal if ids differ).
     * @param updated - The model after the update.
     * @param message - Optional notification message (string or function receiving the updated model).
     * @param notRenderedMessage - Optional message shown when the updated record is not among the rendered rows.
     */
    updateModel(original: M, updated: M, message?: ListActionMessage<M>, notRenderedMessage?: ListActionMessage<M>): void {
        this._deriveModelLabel(updated);
        const id = this._checkRowOrId(updated);
        const rowMessage = this._resolveMessage(message, updated, `${this._modelLabelOrDefault} Updated`);
        this.addRowState(id, {
            type: RowStateType.Highlighted,
            action: CommonRowActions.Update,
            message: rowMessage
        });
        this._app.notificationService.displaySuccessMessage(rowMessage);
        if (this._updateBehavior === ListActionBehavior.Default) {
            this.scrollToRow(id);
            this._pendingNotRenderedAction = {
                id,
                verb: "Updated",
                model: updated,
                notRenderedMessage
            };
        }
        else if (this._updateBehavior === ListActionBehavior.Filter) {
            this.pinModels([updated]);
        }
        this._emitListAction({
            action: CommonRowActions.Update,
            models: [updated],
            ids: [id],
            behavior: this._updateBehavior,
            rowStates: new Map([[id, this.getRowState(id)]]),
            originals: [original]
        });
    }

    /**
     * Records that a model was deleted: removes all row states for that id, displays a
     * success notification, and emits a Delete event on `listAction$`.
     *
     * @param originalOrId - The deleted model or its plain string id.
     * @param message - Optional notification message (string or function receiving the model).
     */
    deleteModel(originalOrId: M | string, message?: ListActionMessage<M>): void {
        const id = this._checkRowOrId(originalOrId);
        if (typeof originalOrId !== "string") {
            this._deriveModelLabel(originalOrId);
        }
        const label = this._modelLabelOrDefault;
        const modelOrUndefined = typeof originalOrId === "string" ? undefined as unknown as M : originalOrId as M;
        const notifyMessage = this._resolveMessage(message, modelOrUndefined, `${label} Deleted`);
        this.removeRowState(id);
        this._app.notificationService.displaySuccessMessage(notifyMessage);
        if (this._deleteBehavior === ListDeleteBehavior.ScrollToSpot) {
            // Captured before the emit: the reload it triggers rebuilds the position map.
            this._scrollToPosition = this._rowPositionById.get(id);
        }
        this._emitListAction({
            action: CommonRowActions.Delete,
            models: typeof originalOrId === "string" ? [] : [originalOrId as M],
            ids: [id],
            behavior: this._deleteBehavior,
            rowStates: new Map()
        });
    }

    /**
     * Marks a row as highlighted without emitting a notification or a list-action event.
     * Accepts a model, a localized row clone, or a plain string id.
     *
     * @param originalOrId - The row to highlight.
     * @param message - Optional message stored in the row state (not shown as a notification).
     */
    highlight(originalOrId: M | R | string, message?: ListActionMessage<M>): void {
        const id = this._checkRowOrId(originalOrId as M | string);
        const rowMessage = this._resolveMessage(
            message,
            ListUtil.unwrapRow<M>(originalOrId),
            this._modelLabelOrDefault
        );
        this.addRowState(id, {
            type: RowStateType.Highlighted,
            action: CommonRowActions.Highlight,
            message: rowMessage
        });
    }

    /**
     * Marks a row as errored. When a message is provided it is also displayed as an
     * error notification. Does not emit on `listAction$`.
     *
     * @param originalOrId - The row that failed.
     * @param message - Optional error message (string or function receiving the model).
     */
    error(originalOrId: M | R | string, message?: ListActionMessage<M>): void {
        const id = this._checkRowOrId(originalOrId as M | string);
        let rowMessage = `${this._modelLabelOrDefault} Error`;
        if (message) {
            rowMessage = this._resolveMessage(message, ListUtil.unwrapRow<M>(originalOrId), rowMessage);
        }
        this.addRowState(id, {
            type: RowStateType.Error,
            action: CommonRowActions.Error,
            message: rowMessage
        });
        if (message) {
            this._app.notificationService.displayErrorMessage(rowMessage);
        }
    }

    /**
     * Records that multiple models were bulk-updated: sets row states and emits notifications.
     *
     * Succeeded rows receive a Highlighted/Bulk row state on the updated model.
     * Failed rows receive an Error/Bulk row state on the original model.
     * Displays one notification for the outcome: success when every row succeeded, error when every
     * row failed, or a single warning joining the success and failure messages for a partial outcome
     * (Material's snackbar shows one message at a time, so separate notifications would replace each
     * other). Emits a Bulk event on `listAction$`.
     *
     * A result that cannot be paired with a model is never dropped: it is counted in the notification
     * (a successful one using its updated model for message functions), logged through
     * `errorHandler.handleServiceError`, and the cache is cleared before the event is emitted so the
     * list reloads the server's data. When every result pairs, behavior is unchanged.
     *
     * @param options - Bulk update options including models, updated models, response, and optional messages.
     */
    bulkUpdateModel(options: IBulkUpdateListActionOptions<M>): void {
        const {
            models, updated, response
        } = options;
        this._deriveModelLabel(models[0] ?? updated[0]);
        const label = this._modelLabelOrDefault;

        const results = this._correlateBulkResults(models, response);

        const succeeded: Array<M> = [];
        const failed: Array<M> = [];
        const succeededOriginals: Array<M> = [];
        const failedOriginals: Array<M> = [];
        const rowStates = new Map<string, IRowState>();
        let unpairedSucceeded = 0;
        let unpairedFailed = 0;
        const unpairedUpdated: Array<M> = [];

        results.forEach(({
            model, item
        }, i) => {
            if (!model) {
                if (item.success) {
                    unpairedSucceeded++;
                    if (updated[i]) {
                        unpairedUpdated.push(updated[i]);
                    }
                }
                else {
                    unpairedFailed++;
                }
                return;
            }
            if (item.success) {
                const updatedModel = updated[i];
                const id = this._checkRowOrId(updatedModel ?? model);
                const rowMsg = this._resolveMessage(options.message, updatedModel ?? model, `${label} Updated`);
                this.addRowState(id, {
                    type: RowStateType.Highlighted,
                    action: CommonRowActions.Bulk,
                    message: rowMsg
                });
                succeeded.push(updatedModel ?? model);
                succeededOriginals.push(model);
                rowStates.set(id, this.getRowState(id));
            }
            else {
                const id = this._checkRowOrId(model);
                const itemError = item.error?.message ?? "";
                const rowMsg = this._resolveMessage(options.error, model, `${label} could not be updated${itemError ? `: ${itemError}` : ""}`);
                this.addRowState(id, {
                    type: RowStateType.Error,
                    action: CommonRowActions.Bulk,
                    message: rowMsg
                });
                failed.push(model);
                failedOriginals.push(model);
                rowStates.set(id, this.getRowState(id));
            }
        });

        const unpaired = unpairedSucceeded + unpairedFailed;
        if (unpaired) {
            this._app.errorHandler.handleServiceError(
                new Error(`bulkUpdateModel: ${unpaired} of ${results.length} result(s) could not be paired with a model; reloading the list.`)
            );
        }

        const successCount = succeeded.length + unpairedSucceeded;
        const failureCount = failed.length + unpairedFailed;
        this._notifyBulkOutcome(
            successCount ? this._resolveMessage(options.notificationMessage, [...succeeded, ...unpairedUpdated], `${successCount} ${label}(s) updated`) : undefined,
            failureCount ? this._resolveMessage(options.errorNotification, failed, `${failureCount} of ${models.length} ${label}(s) could not be updated`) : undefined
        );

        if (this._updateBehavior === ListActionBehavior.Default && succeeded.length) {
            const firstSucceeded = succeeded[0];
            const firstId = this._checkRowOrId(firstSucceeded);
            this.scrollToRow(firstId);
            this._pendingNotRenderedAction = {
                id: firstId,
                verb: "Updated",
                model: firstSucceeded,
                notRenderedMessage: options.notRenderedMessage
            };
        }
        else if (this._updateBehavior === ListActionBehavior.Filter && succeeded.length) {
            this.pinModels(succeeded);
        }

        if (unpaired) {
            // The cache cannot be patched without the originals; drop it so the list reloads.
            this.clearCache();
        }

        this._emitListAction({
            action: CommonRowActions.Bulk,
            models: [...succeeded, ...failed],
            ids: [...rowStates.keys()],
            behavior: this._updateBehavior,
            rowStates,
            originals: [...succeededOriginals, ...failedOriginals]
        });
    }

    /**
     * Records that multiple models were bulk-deleted: sets row states and emits notifications.
     *
     * Succeeded rows have their row states removed. Failed rows receive an Error/Bulk row state.
     * Displays one notification for the outcome: success when every row succeeded, error when every
     * row failed, or a single warning joining the success and failure messages for a partial outcome
     * (Material's snackbar shows one message at a time, so separate notifications would replace each
     * other). Emits a Bulk event on `listAction$`.
     *
     * @param options - Bulk delete options including models, response, and optional messages.
     */
    bulkDeleteModel(options: IBulkListActionOptions<M>): void {
        const {
            models, response
        } = options;
        this._deriveModelLabel(models[0]);
        const label = this._modelLabelOrDefault;

        const results = this._correlateBulkResults(models, response);

        const succeeded: Array<M> = [];
        const failed: Array<M> = [];
        const rowStates = new Map<string, IRowState>();

        results.forEach(({
            model, item
        }) => {
            if (!model) {
                return;
            }
            const id = this._checkRowOrId(model);
            if (item.success) {
                this.removeRowState(id);
                succeeded.push(model);
            }
            else {
                const itemError = item.error?.message ?? "";
                const rowMsg = this._resolveMessage(options.error, model, `${label} could not be deleted${itemError ? `: ${itemError}` : ""}`);
                this.addRowState(id, {
                    type: RowStateType.Error,
                    action: CommonRowActions.Bulk,
                    message: rowMsg
                });
                failed.push(model);
                rowStates.set(id, this.getRowState(id));
            }
        });

        this._notifyBulkOutcome(
            succeeded.length ? this._resolveMessage(options.notificationMessage, succeeded, `${succeeded.length} ${label}(s) deleted`) : undefined,
            failed.length ? this._resolveMessage(options.errorNotification, failed, `${failed.length} of ${models.length} ${label}(s) could not be deleted`) : undefined
        );

        const succeededIds = succeeded.map((m) => this._checkRowOrId(m));
        if (this._deleteBehavior === ListDeleteBehavior.ScrollToSpot) {
            // Captured before the emit: the reload it triggers rebuilds the position map.
            this._scrollToPosition = this._lowestRowPosition(succeededIds);
        }
        this._emitListAction({
            action: CommonRowActions.Bulk,
            models: [...succeeded, ...failed],
            ids: succeededIds,
            behavior: this._deleteBehavior,
            rowStates
        });
    }

    /**
     * Returns the id for this row
     * @param row;
     */
    protected abstract _getRowId(row: M): string;

    /**
     * Returns the row id from a rowOrId parameter.
     * If the row is a localized clone (has an `originalModel` property), unwraps it
     * to the original model before resolving the id.
     */
    protected _checkRowOrId(rowOrId: string | M | R): string {

        if (StringUtil.isString(rowOrId)) {
            return rowOrId as string;
        }
        const row = ListUtil.unwrapRow<M>(rowOrId);
        if (row instanceof Object) {
            return this._getRowId(row);
        }

        throw new InvalidParametersException("rowOrId is required");
    }

    /**
     * Correlates bulk response results with their originating models by index.
     *
     * When the response result count matches the model count, items are paired by position.
     * When counts differ, falls back to id-based correlation via `item.payload`, logs the
     * mismatch via `errorHandler.handleServiceError`, and attempts to match each result to
     * a model by id. Models that cannot be matched yield `undefined` in the `model` field.
     *
     * @param models - The original models that were submitted in the bulk request.
     * @param response - The bulk response returned by the API.
     * @returns An index-correlated array of `{ model, item }` pairs.
     */
    protected _correlateBulkResults(
        models: Array<M>,
        response: BulkResponse
    ): Array<{
            model: M | undefined;
            item: BulkResponseItem;
        }> {
        if (response.results.length !== models.length) {
            this._app.errorHandler.handleServiceError(
                new Error(
                    `bulkUpdateModel: models.length (${models.length}) !== results.length (${response.results.length}). Falling back to id correlation.`
                )
            );
            return response.results.map((item) => {
                const id = this._checkRowOrId((item.payload as M) ?? "");
                const model = models.find((m) => this._getRowId(m) === id);
                return {
                    model,
                    item: item as BulkResponseItem
                };
            });
        }
        return response.results.map((item, i) => ({
            model: models[i],
            item: item as BulkResponseItem
        }));
    }

    /**
     * Applies the cache invalidation matrix, then emits the event on `listAction$`.
     *
     * The matrix is applied here, not in the data source, because the data source is scoped to the
     * list component and is destroyed while the user is on another route (such as a domain form).
     * An action recorded in that window must still update the cache, or the re-created list would
     * be served stale results from the cache.
     *
     * **Invalidation matrix** (cached lists with a cache entry only):
     *
     * | Event                                   | Unpaged              | Paged        |
     * |-----------------------------------------|----------------------|--------------|
     * | createModel                             | cacheInsert + resort | clearCache() |
     * | deleteModel / bulkDeleteModel           | cacheRemove per id   | clearCache() |
     * | updateModel, sort field changed         | cacheReplace+resort  | clearCache() |
     * | updateModel, no sort change             | cacheReplace         | cacheReplace |
     * | bulkUpdateModel, any sort field changed | cacheReplace+resort  | clearCache() |
     * | bulkUpdateModel, no sort change         | cacheReplace each    | cacheReplace each |
     * | bulkUpdateModel, unpaired result        | clearCache()         | clearCache() (before emit) |
     *
     * A subscribed data source then reloads. When the cache was mutated in place the reload finds a
     * cache hit and returns without a network call; when it was cleared the reload fetches fresh data.
     *
     * @param event - The list action event to apply and emit.
     */
    private _emitListAction(event: IListActionEvent<M>): void {
        if (this._cached && this._cacheEntry) {
            this._applyCacheMatrix(event);
        }
        this._listAction$.next(event);
    }

    /**
     * Applies the cache invalidation matrix for the given list action event.
     * Called only when caching is enabled and a cache entry exists.
     *
     * @param event - The list action event to process.
     */
    private _applyCacheMatrix(event: IListActionEvent<M>): void {
        const paged = this._paged;
        const currentSort = this.sort;

        switch (event.action) {
            case CommonRowActions.Create:
                if (!paged) {
                    // Unpaged: insert each created model; cacheInsert already re-sorts.
                    event.models.forEach((m) => this.cacheInsert(m));
                }
                else {
                    // Paged: cannot determine the correct page placement — clear.
                    this.clearCache();
                }
                break;

            case CommonRowActions.Delete:
                // deleteModel puts the deleted model's id in event.ids.
                if (!paged) {
                    event.ids.forEach((id) => this.cacheRemove(id));
                }
                else {
                    this.clearCache();
                }
                break;

            case CommonRowActions.Bulk:
                if (event.originals) {
                    // bulkUpdateModel: originals present — this is a bulk update.
                    this._applyBulkUpdateCacheMatrix(event, event.originals, paged, currentSort);
                }
                else {
                    // bulkDeleteModel: no originals — ids holds succeeded model ids.
                    if (!paged) {
                        event.ids.forEach((id) => this.cacheRemove(id));
                    }
                    else {
                        this.clearCache();
                    }
                }
                break;

            case CommonRowActions.Update: {
                // updateModel: originals[0] is the pre-update model; models[0] is updated.
                const original = event.originals?.[0];
                const updated = event.models[0];
                if (original && updated) {
                    const sortChanged = currentSort && ListSortUtil.sortFieldChanged(currentSort, original, updated);
                    if (!sortChanged) {
                        // No sort change: replace in place on both paged and unpaged.
                        this.cacheReplace(original, updated);
                    }
                    else if (!paged) {
                        // Sort changed, unpaged: replace then resort.
                        this.cacheReplace(original, updated);
                        this.cacheResort();
                    }
                    else {
                        // Sort changed, paged: clear.
                        this.clearCache();
                    }
                }
                break;
            }

            default:
                break;
        }
    }

    /**
     * Applies the cache invalidation sub-matrix for a bulk-update event.
     *
     * @param event - The bulk list action event (action === Bulk, originals present).
     * @param originals - The pre-update models, indexed in parallel with `event.models`.
     * @param paged - Whether the list is in paged mode.
     * @param currentSort - The currently active sort option.
     */
    private _applyBulkUpdateCacheMatrix(
        event: IListActionEvent<M>,
        originals: Array<M>,
        paged: boolean,
        currentSort: IListSortOption
    ): void {
        const sortChanged = event.models.some((m, i) => {
            const original = originals[i] ?? m;
            return currentSort && ListSortUtil.sortFieldChanged(currentSort, original, m);
        });

        if (!sortChanged) {
            // No sort change: replace each in place on both paged and unpaged.
            event.models.forEach((m, i) => {
                const original = originals[i] ?? m;
                this.cacheReplace(original, m);
            });
        }
        else if (!paged) {
            // Sort changed, unpaged: replace all then resort once.
            event.models.forEach((m, i) => {
                const original = originals[i] ?? m;
                this.cacheReplace(original, m);
            });
            this.cacheResort();
        }
        else {
            // Sort changed, paged: clear.
            this.clearCache();
        }
    }

    /**
     * Returns the resolved model label, falling back to "Record" when none is set.
     */
    private get _modelLabelOrDefault(): string {
        return this._modelLabel ?? "Record";
    }

    /**
     * Derives the model label from the model instance's constructor `modelName` static property.
     * No-op if a label has already been derived.
     *
     * @param instance - Any value; only objects with `constructor.modelName` yield a label.
     */
    private _deriveModelLabel(instance: unknown): void {
        if (this._modelLabel !== undefined) {
            return;
        }
        const modelName = (instance as any)?.constructor?.modelName as string | undefined;
        this._modelLabel = modelName ? ListUtil.modelLabel(modelName) : undefined;
    }

    /**
     * Resolves a `ListActionMessage` to a plain string.
     *
     * @param message - Static string, function, or undefined.
     * @param value - Value passed to the function variant.
     * @param fallback - Returned when `message` is undefined.
     */
    private _resolveMessage<T>(message: ListActionMessage<T> | undefined, value: T, fallback: string): string {
        if (message === undefined) {
            return fallback;
        }
        if (typeof message === "function") {
            return message(value);
        }
        return message;
    }

    /**
     * Displays one notification for a bulk outcome. Both messages present (partial outcome) produce
     * a single warning joining them; otherwise the lone message is shown as a success or an error.
     *
     * @param successMessage - Message for the succeeded rows, or `undefined` when none succeeded.
     * @param errorMessage - Message for the failed rows, or `undefined` when none failed.
     */
    private _notifyBulkOutcome(successMessage: string | undefined, errorMessage: string | undefined): void {
        if (successMessage && errorMessage) {
            this._app.notificationService.displayWarningMessage(`${successMessage}; ${errorMessage}`);
        }
        else if (successMessage) {
            this._app.notificationService.displaySuccessMessage(successMessage);
        }
        else if (errorMessage) {
            this._app.notificationService.displayErrorMessage(errorMessage);
        }
    }

    /**
     * Resolves a pending scroll-to-position against the freshly recorded row positions and
     * hands it to {@link scrollToRow}. The pending position is always cleared, so a position
     * that cannot be resolved does not carry over to a later load.
     *
     * The position is clamped to the last row, which is what makes deleting the final row
     * land on the new final row. No scroll occurs when the list is empty.
     */
    private _resolveScrollToPosition(): void {
        const position = this._scrollToPosition;
        this._scrollToPosition = undefined;
        if (position === undefined || !this._rowIdByPosition.length) {
            return;
        }
        const clamped = Math.min(Math.max(position, 0), this._rowIdByPosition.length - 1);
        this.scrollToRow(this._rowIdByPosition[clamped]);
    }

    /**
     * Returns the lowest recorded position among the given row ids, or `undefined` when none
     * of them are among the rendered rows. Used by a bulk delete so the scroll lands at the
     * top of the deleted block.
     */
    private _lowestRowPosition(ids: Array<string>): number | undefined {
        let lowest: number | undefined;
        ids.forEach((id) => {
            const position = this._rowPositionById.get(id);
            if (position !== undefined && (lowest === undefined || position < lowest)) {
                lowest = position;
            }
        });
        return lowest;
    }

    /**
     * Validates and applies shared initialization options common to all six initializers.
     */
    private _applyInitOptions(options: IListInitOptions): void {
        if (!options.sortOptions?.length) {
            throw new InvalidParametersException("sortOptions is required");
        }
        if (options.defaultSortOption !== undefined && !ArrayUtil.contains(options.defaultSortOption, options.sortOptions)) {
            throw new InvalidParametersException("defaultSortOption must be one of sortOptions");
        }
        if (options.cached) {
            for (const opt of options.sortOptions) {
                if (!ListSortUtil.comparator(opt)) {
                    throw new InvalidParametersException(
                        `Sort option "${opt.label}" has no comparator. A cached list requires every sort option to be locally orderable.`
                    );
                }
            }
        }
        this._sortOptions = options.sortOptions;
        this._defaultSortOption = options.defaultSortOption ?? undefined;
        this._cached = options.cached ?? false;
        this._createBehavior = options.createBehavior ?? ListActionBehavior.Default;
        this._updateBehavior = options.updateBehavior ?? ListActionBehavior.Default;
        this._deleteBehavior = options.deleteBehavior ?? ListDeleteBehavior.Default;
        this._applySort(this._defaultSortOption);
    }

    /**
     * Applies the given sort option to the sort subject without resetting the page index.
     * Used during initialization before paging is set up.
     */
    private _applySort(sortOption?: IListSortOption): void {
        this._sort$.next(sortOption ?? undefined);
    }

    /**
     * Checks the shared initialization state (internal helper used by shared list initializers).
     */
    private _checkSharedInitialized(searchList: boolean): boolean {
        return searchList ? this.searchInitialized : this.listInitialized;
    }

    /**
     * Update the info header state
     *
     * Should be called when the rowState is changed
     */
    private _updateInfoHeaderState(): void {

        let state: IRowState;
        if (this.hasRowState(RowStateType.Error)) {
            state = {
                type: RowStateType.Error,
                action: CommonRowActions.Validate,
                message: "There are errors in the list"
            };
        }
        else if (this.hasRowState(RowStateType.Warning)) {
            state = {
                type: RowStateType.Warning,
                action: CommonRowActions.Validate,
                message: "There are warnings in the list"
            };
        }
        else {
            state = undefined;
        }

        if (this.infoHeaderState?.type !== state?.type) {
            this._infoHeaderState$.next(state);
        }
    }

    /**
     * Creates a new filter request from the provided parameters.
     * Returns an IPagedListFilterRequest when paged, IUnpagedListFilterRequest when not paged,
     * or undefined when filterOptions is not set.
     */
    private _newFilterRequest(options: O, pageIndex: number, pageSize: number, sort: IListSortOption): IListFilterRequest<O> {
        if (options === undefined || options === null) {
            return undefined;
        }
        if (this._paged) {
            const pagedRequest: IPagedListFilterRequest<O> = {
                paged: true,
                pageIndex,
                pageSize,
                from: ListUtil.getFromValue(pageIndex, pageSize),
                options,
                sort
            };
            return pagedRequest;
        }
        const unpagedRequest: IUnpagedListFilterRequest<O> = {
            paged: false,
            options,
            sort
        };
        return unpagedRequest;
    }

    private _removeRowState(rowOrId: string | M | R, action: CommonRowActions): void {
        const id = this._checkRowOrId(rowOrId);
        if (action) {
            this._rowStateById.set(id, this._rowStateById.get(id)?.filter((state) => state.action !== action));
        }
        else {
            this._rowStateById.delete(id);
        }
        this._updateInfoHeaderState();
    }
}
